import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash';
import { format, getUnixTime, isValid } from 'date-fns';
import { Pane } from '../../../../core/components/SplitPanes.tsx';
import Loader from '../../../../core/components/Loader.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faArrowRight, faCheckCircle, faSpinnerThird, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import classnames from '../../../../core/utils/classnames.tsx';

const formatDate = (date) => {
    const dateObj = date ? new Date(date) : null;
    return dateObj && isValid(dateObj) ? format(dateObj, 'dd.MM.yyyy, HH:mm:ss') : undefined;
};

const EXECUTION_STATUS_MAP = {
    PENDING: 'pending',
    SCHEDULED: 'pending',
    RUNNING: 'pending',
    WAITING_FOR_MANUAL: 'pending',
    SUCCEEDED: 'success',
    FAILED: 'error',
};

const LoadingIcon = withIcon(faSpinnerThird);
const SuccessIcon = withIcon(faCheckCircle);
const ErrorIcon = withIcon(faXmarkCircle);
const ArrowRightIcon = withIcon(faArrowRight);

const Status = ({ status, onRetryStep }) => {
    const { t } = useTranslation('assistance');

    const stepName = (status?.stepName && camelCase(status?.stepName)) || 'finished';
    const statusClassName = EXECUTION_STATUS_MAP[status?.executionStatus] || 'pending';

    const context = (status?.context && JSON.parse(status?.context)) || {};
    const fields = {
        createdAt: formatDate(status?.createdAt),
        updatedAt: formatDate(status?.updatedAt),
        ...context?.details,
    };

    return (
        <>
            <div className="flex flex-col gap-2">
                <div className="mb-2">
                    <div
                        className={classnames(
                            'text-content-weak text-sm font-medium flex gap-2 items-center',
                            statusClassName === 'error' && 'text-content-danger-weak',
                            statusClassName === 'success' && 'text-content-success-weak'
                        )}
                    >
                        {statusClassName === 'pending' && <LoadingIcon className="text-content-success-weak" spin />}
                        {statusClassName === 'success' && <SuccessIcon />}
                        {statusClassName === 'error' && <ErrorIcon />}
                        <div className="text-content-strong">
                            {statusClassName && t(`resultView.steps.${stepName}`)}
                        </div>
                    </div>
                </div>

                <div className="flex flex-row pl-5.5 gap-2 w-full">
                    <div className="flex flex-col w-full gap-4">
                        <div className="flex flex-row items-start gap-2 w-full">
                            {Object.entries(fields)
                                .slice(0, 2)
                                .map(([fieldName, fieldValue], index, arr) => (
                                    <React.Fragment key={fieldName}>
                                        <FormField
                                            label={t(`resultView.fields.${fieldName}`)}
                                            disabled
                                            className="flex flex-col flex-1"
                                        >
                                            <StringField value={fieldValue as string} disabled className="w-full" />
                                        </FormField>

                                        {arr.length >= 2 && index === 0 && (
                                            <ArrowRightIcon className="text-content-black text-sm self-center pt-6" />
                                        )}
                                    </React.Fragment>
                                ))}
                        </div>

                        {Object.entries(fields)
                            .slice(2)
                            .map(([fieldName, fieldValue]) => (
                                <div key={fieldName} className="flex flex-col w-full">
                                    <FormField
                                        label={t(`resultView.fields.${fieldName}`)}
                                        disabled
                                        className="flex flex-col flex-grow"
                                    >
                                        <StringField value={fieldValue as string} disabled className="w-full" />
                                    </FormField>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </>
    );
};

const ResultView = (props) => {
    const { record, stepRunList, onRetryStep } = props;
    // make sure they are in the right order
    const sortedStepRunList = stepRunList?.sort((a, b) => {
        const aCreatedAt = getUnixTime(new Date(a.createdAt));
        const bCreatedAt = getUnixTime(new Date(b.createdAt));
        const aUpdatedAt = getUnixTime(new Date(a.updatedAt));
        const bUpdatedAt = getUnixTime(new Date(b.updatedAt));

        if (aCreatedAt > bCreatedAt) {
            return 1;
        }
        if (aCreatedAt < bCreatedAt) {
            return -1;
        }
        if (aUpdatedAt > bUpdatedAt) {
            return 1;
        }
        if (aUpdatedAt < bUpdatedAt) {
            return -1;
        }
        return 0;
    });

    return (
        <div>
            <Pane>
                <div className="flex flex-col gap-8">
                    {sortedStepRunList.length === 0 ? (
                        <Loader />
                    ) : (
                        sortedStepRunList?.map((step) => (
                            <Status key={step.id} status={step} onRetryStep={onRetryStep} />
                        ))
                    )}
                </div>
            </Pane>
        </div>
    );
};

export default ResultView;
